import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { format, isAfter, isBefore, startOfDay, endOfDay } from "date-fns";
import { RefreshCw, CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";

/* ── Unified row shape ── */
interface UnifiedAuditRow {
  id: string;
  source: "admin" | "auth";
  user_id: string | null;
  email: string | null;
  event: string;          // human-readable
  event_raw: string;      // original value for filtering
  timestamp: string;
  detail: string | null;  // reason / metadata
}

export const AdminAuditLogTable = () => {
  const [rows, setRows] = useState<UnifiedAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  const { toast } = useToast();

  /* ── Load both sources in parallel ── */
  const loadLogs = async () => {
    try {
      setLoading(true);

      const [adminRes, authRes] = await Promise.all([
        (supabase as any).rpc("admin_list_audit_log", { p_limit: 500 }),
        (supabase as any).rpc("admin_list_auth_audit_logs", {
          p_limit: 200,
          p_event_type: null,
          p_email_filter: null,
        }),
      ]);

      const adminRows: UnifiedAuditRow[] = (adminRes.data || []).map((r: any) => ({
        id: r.id,
        source: "admin" as const,
        user_id: r.user_id,
        email: null,
        event: humanizeAdminAction(r.action),
        event_raw: r.action,
        timestamp: r.created_at,
        detail: r.reason || (r.table_name ? `Table: ${r.table_name}` : null),
      }));

      const authRows: UnifiedAuditRow[] = (authRes.data || []).map((r: any) => ({
        id: r.out_id,
        source: "auth" as const,
        user_id: r.out_user_id,
        email: r.out_email,
        event: humanizeAuthEvent(r.out_event_type),
        event_raw: r.out_event_type,
        timestamp: r.out_created_at,
        detail: r.out_metadata
          ? typeof r.out_metadata === "object"
            ? Object.entries(r.out_metadata).map(([k, v]) => `${k}: ${v}`).join(", ")
            : String(r.out_metadata)
          : null,
      }));

      const merged = [...adminRows, ...authRows].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setRows(merged);

      if (adminRes.error) console.error("Admin audit error:", adminRes.error);
      if (authRes.error) console.error("Auth audit error:", authRes.error);
    } catch (error) {
      console.error("Error loading audit logs:", error);
      toast({ title: "Error", description: "Failed to load audit logs", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, []);

  /* ── Helpers ── */
  const humanizeAdminAction = (action: string) => {
    return action
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const humanizeAuthEvent = (eventType: string) => {
    switch (eventType) {
      case "login_success": return "Login Success";
      case "login_failed": return "Login Failed";
      case "logout": return "Logout";
      default: return eventType;
    }
  };

  const getBadgeVariant = (row: UnifiedAuditRow): "default" | "secondary" | "destructive" | "outline" => {
    if (row.source === "auth") {
      if (row.event_raw === "login_success") return "default";
      if (row.event_raw === "login_failed") return "destructive";
      return "secondary";
    }
    if (row.event_raw.includes("CREATE")) return "default";
    if (row.event_raw.includes("UPDATE") || row.event_raw.includes("GRANT")) return "secondary";
    if (row.event_raw.includes("DELETE") || row.event_raw.includes("REVOKE")) return "destructive";
    return "outline";
  };

  const getSourceLabel = (source: string) =>
    source === "auth" ? "Auth" : "Action";

  /* ── Filtered rows ── */
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Category filter
      if (categoryFilter === "authentication" && row.source !== "auth") return false;
      if (categoryFilter === "user_actions" && row.source !== "admin") return false;

      // Text search
      if (searchFilter) {
        const q = searchFilter.toLowerCase();
        const matchesSearch =
          row.event.toLowerCase().includes(q) ||
          row.email?.toLowerCase().includes(q) ||
          row.user_id?.toLowerCase().includes(q) ||
          row.detail?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      // Date range
      const ts = new Date(row.timestamp);
      if (dateFrom && isBefore(ts, startOfDay(dateFrom))) return false;
      if (dateTo && isAfter(ts, endOfDay(dateTo))) return false;

      return true;
    });
  }, [rows, categoryFilter, searchFilter, dateFrom, dateTo]);

  if (loading && rows.length === 0) {
    return <div className="flex justify-center p-8 text-muted-foreground">Loading audit logs...</div>;
  }

  return (
    <div className="space-y-4">
      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-3 items-center">
        <Input
          placeholder="Search by email, event, detail..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          className="max-w-xs"
        />

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="user_actions">User Actions</SelectItem>
            <SelectItem value="authentication">Authentication</SelectItem>
          </SelectContent>
        </Select>

        {/* Date From */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("w-[150px] justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}>
              <CalendarIcon className="h-4 w-4 mr-1" />
              {dateFrom ? format(dateFrom, "PP") : "From"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus className={cn("p-3 pointer-events-auto")} />
          </PopoverContent>
        </Popover>

        {/* Date To */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("w-[150px] justify-start text-left font-normal", !dateTo && "text-muted-foreground")}>
              <CalendarIcon className="h-4 w-4 mr-1" />
              {dateTo ? format(dateTo, "PP") : "To"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus className={cn("p-3 pointer-events-auto")} />
          </PopoverContent>
        </Popover>

        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(undefined); setDateTo(undefined); }}>
            <X className="h-4 w-4 mr-1" /> Clear dates
          </Button>
        )}

        <Button variant="outline" size="sm" onClick={loadLogs} disabled={loading} className="ml-auto">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* ── Summary ── */}
      <div className="text-xs text-muted-foreground">
        Showing {filteredRows.length} of {rows.length} events
      </div>

      {/* ── Table ── */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow key={`${row.source}-${row.id}`}>
                <TableCell>
                  <div className="text-sm whitespace-nowrap">
                    {format(new Date(row.timestamp), "PPp")}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {getSourceLabel(row.source)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={getBadgeVariant(row)}>
                    {row.event}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {row.email || (
                      <span className="font-mono text-xs text-muted-foreground">
                        {row.user_id ? row.user_id.slice(0, 8) + "..." : "System"}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="max-w-xs truncate text-sm text-muted-foreground" title={row.detail || undefined}>
                    {row.detail || "—"}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredRows.length === 0 && !loading && (
        <div className="text-center p-8 text-muted-foreground">
          No audit logs found matching your criteria.
        </div>
      )}
    </div>
  );
};
